import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { PerfectScrollbarConfigInterface } from "ngx-perfect-scrollbar";
import { GiFinSimulacaoFinanciadoraModel } from "../../../dominio/gi-fin-simulacao-financiadora-model";
import { GiFinMotivoNegativaModel } from "../../../dominio/gi-fin-motivo-negativa-model";
import { GiFinSituacaoModel } from "../../../dominio/gi-fin-situacao-model";
import { FinSimulacaoFinanciadoraStatus } from "../../../dominio/enums";
import { FinSimulacaoService, JustificativaFinanciadoraRequest } from "../../../services/fin-simulacao.service";
import { NotificacaoService } from "../../../services/notificacao.service";
import { CarregandoService } from "../../../services/carregando.service";
import { QueryFilterService } from "../../../core/services/query-filter-service";
import { ArquivoService } from "../../../services/arquivo.service";
import { UtilitarioService } from "../../../services/utilitario.service";
import { TipoArquivo } from "../../../models/enum";
import { MotivoNegativaConfigService, opcoesMotivoNegativa } from "../../services/motivo-negativa-config.service";

export interface JustificativaConclusaoSimulacaoResult {
  financiadoras: JustificativaFinanciadoraRequest[];
}

// Financiadoras que ainda não responderam continuam em aberto na conclusão positiva, sem motivo de negativa
const SITUACOES_EM_ABERTO = [
  FinSimulacaoFinanciadoraStatus.Pendente,
  FinSimulacaoFinanciadoraStatus.Solicitando,
  FinSimulacaoFinanciadoraStatus.DadosSolicitacao,
];

interface FinanciadoraJustificar {
  vinculo: GiFinSimulacaoFinanciadoraModel;
  situacao: FinSimulacaoFinanciadoraStatus;
  idMotivoNegativa: number | null;
  motivos: GiFinMotivoNegativaModel[];
}

@Component({
  selector: "app-justificativa-conclusao-simulacao-modal",
  templateUrl: "./justificativa-conclusao-simulacao-modal.component.html",
  styleUrls: ["./justificativa-conclusao-simulacao-modal.component.scss"],
})
export class JustificativaConclusaoSimulacaoModalComponent implements OnInit {
  config: PerfectScrollbarConfigInterface = {};
  idTarefa: number;
  disponiveis: GiFinSimulacaoFinanciadoraModel[] = [];
  emAberto: GiFinSimulacaoFinanciadoraModel[] = [];
  financiadoras: FinanciadoraJustificar[] = [];
  // Na conclusão positiva só é possível justificar: "Disponível" e as situações em aberto não são oferecidas
  situacoes: GiFinSituacaoModel[] = [];

  constructor(
    private dialogRef: MatDialogRef<JustificativaConclusaoSimulacaoModalComponent, JustificativaConclusaoSimulacaoResult>,
    @Inject(MAT_DIALOG_DATA) public data: { idTarefa: number },
    private finSimulacaoService: FinSimulacaoService,
    private motivoNegativaService: MotivoNegativaConfigService,
    private notificacaoService: NotificacaoService,
    private carregandoService: CarregandoService,
    private queryFilterService: QueryFilterService,
    private arquivoService: ArquivoService,
    private utilitarioService: UtilitarioService,
  ) {
    this.idTarefa = data.idTarefa;
  }

  get podeConfirmar() {
    return this.financiadoras.every((f) => !!f.idMotivoNegativa && f.situacao !== null && f.situacao !== undefined);
  }

  ngOnInit() {
    this.buscarDados();
  }

  buscarDados() {
    this.carregandoService.mostra("buscarFinanciadorasConclusao");

    const filtroSituacoes = this.queryFilterService.getQueryBuilder()
      .addCondition("tipoSituacao", "=", 1)
      .build();

    forkJoin([
      this.finSimulacaoService.buscarFinanciadorasPorTarefa(this.idTarefa),
      this.motivoNegativaService.buscarAtivos(),
      this.finSimulacaoService.buscarListaStatus(filtroSituacoes),
    ]).subscribe(
      ([financiadoras, motivos, situacoes]) => {
        const ativos = motivos.results || [];
        // Financiadoras ocultas para o integrador não entram no modal
        const visiveis = (financiadoras.results || []).filter((v) => !!v.visivel);

        this.situacoes = (situacoes.results || [])
          .filter((s) => s.idSituacao !== FinSimulacaoFinanciadoraStatus.Disponivel && !SITUACOES_EM_ABERTO.includes(s.idSituacao));
        this.disponiveis = visiveis.filter((v) => v.status === FinSimulacaoFinanciadoraStatus.Disponivel);
        this.emAberto = visiveis.filter((v) => SITUACOES_EM_ABERTO.includes(v.status));
        this.financiadoras = visiveis
          .filter((v) => v.status !== FinSimulacaoFinanciadoraStatus.Disponivel && !SITUACOES_EM_ABERTO.includes(v.status))
          .map((vinculo) => ({
            vinculo,
            situacao: vinculo.status,
            idMotivoNegativa: vinculo.idMotivoNegativa || null,
            motivos: opcoesMotivoNegativa(ativos, vinculo),
          }));

        visiveis.forEach((v) => {
          this.buscarSumario(v);
          this.buscarLogo(v);
        });

        this.carregandoService.esconde("buscarFinanciadorasConclusao");
      },
      (erro) => {
        this.carregandoService.esconde("buscarFinanciadorasConclusao");
        this.notificacaoService.erro(erro);
      }
    );
  }

  buscarSumario(vinculo: GiFinSimulacaoFinanciadoraModel) {
    this.finSimulacaoService.buscaSumarioFinanciadoraCelulaCredito(this.idTarefa, vinculo.id).subscribe(
      (data) => {
        vinculo.sumario = data.result;
      },
      () => { }
    );
  }

  buscarLogo(vinculo: GiFinSimulacaoFinanciadoraModel) {
    const financiadora = vinculo.financiadora;

    if (!financiadora) {
      return;
    }

    financiadora.imagem$ = this.arquivoService
      .baixarArquivo(financiadora.id, TipoArquivo.FinanciadoraImagem)
      .pipe(map((m) => this.utilitarioService.toBase64ComInicio(m)))
      .toPromise();

    financiadora.imagem$
      .then(() => financiadora.buscouImagem = true)
      .catch(() => financiadora.semImagem = true);
  }

  confirmar() {
    if (!this.podeConfirmar) {
      return;
    }

    this.dialogRef.close({
      financiadoras: this.financiadoras.map((f) => ({
        idVinculo: f.vinculo.id,
        exibir: true,
        idMotivoNegativa: f.idMotivoNegativa,
        situacao: f.situacao !== f.vinculo.status ? f.situacao : null,
      })),
    });
  }

  fechar() {
    this.dialogRef.close();
  }
}
